import { MenuItem } from "@/model/entities";
import {
  closestCenter,
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { useCallback } from "react";
import { SortableItem } from "./SortableItem";
import MenuItemCard4 from "./MenuItemCard4";

interface SortableMenuItemListProps {
  items: MenuItem[];
  onEdit: (item: MenuItem) => void;
  onDelete: (item: MenuItem) => void;
  onReorder?: (reorderedItems: MenuItem[]) => void;
}

const SortableMenuItemList: React.FC<SortableMenuItemListProps> = ({
  items,
  onEdit,
  onDelete,
  onReorder,
}) => {
  // Setup sensors for drag detection
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );
  // Handle drag end event
  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;

      if (over && active.id !== over.id) {
        const oldIndex = items.findIndex((item) => item.id === active.id);
        const newIndex = items.findIndex((item) => item.id === over.id);

        const reorderedMenuItems = arrayMove(items, oldIndex, newIndex);
        onReorder?.(reorderedMenuItems);
      }
    },
    [items, onReorder]
  );

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
    >
      <SortableContext
        items={items
          .filter((item) => item.id !== undefined)
          .map((item) => item.id as string)}
        strategy={verticalListSortingStrategy}
      >
        <div className="grid grid-cols-1 gap-4">
          {items.map((item) => (
            <SortableItem key={item.id} id={item.id!}>
              <MenuItemCard4
                key={item.id}
                menuItem={item}
                onEdit={onEdit}
                onDelete={onDelete}
              />
            </SortableItem>
          ))}
        </div>
      </SortableContext>
    </DndContext>
  );
};

export default SortableMenuItemList;
